import CheckoutReviewOrderDetailsAddressDisplay from "@/app/checkout/(squeeze)/review/checkoutReviewOrderDetailsAddressDisplay";
import useCheckout from "@/app/hooks/useCheckout";
import { useGetMyLicensesQuery, useGetUserNameDetailsQuery } from "@/store/userApi";
import Image from "next/image";
import Link from "next/link";
import styles from "./checkoutReviewOrderDetails.module.scss";

export default function CheckoutReviewOrderDetails({ isConfirmationPage }: { isConfirmationPage: boolean }) {
  const { getSelectedShippingAddress, getSelectedCreditCard } = useCheckout();
  const chosenShippingAddress = getSelectedShippingAddress();
  const { data: nameData, isLoading: userNameDataLoading } = useGetUserNameDetailsQuery();
  const chosenCreditCard = getSelectedCreditCard();
  // Licenses come from the users service, not windfall's session-cached /user blob.
  const { data: myLicenses = [] } = useGetMyLicensesQuery();
  const { path, alt } = getCreditCardLogoDetails(chosenCreditCard?.Type);

  function getCreditCardLogoDetails(creditCardType: string | undefined): { path: string; alt: string } {
    switch (creditCardType) {
      case "AMEX":
        return { path: "/media/shared/common/icons/amex.svg", alt: "[American Express]" };
      case "Discover":
        return { path: "/media/shared/common/icons/discover.svg", alt: "[Discover]" };
      case "MasterCard":
        return { path: "/media/shared/common/icons/mastercard.svg", alt: "[MasterCard]" };
      case "Visa":
        return { path: "/media/shared/common/icons/visa.svg", alt: "[Visa]" };
      default:
        return { path: "", alt: "" };
    }
  }

  return (
    <div className={styles.OrderDetails}>
      <div className={styles.infoGrid}>
        <div className={styles.column}>
          <div className={styles.sectionHeader}>Shipping Address</div>
          {chosenShippingAddress ? (
            <div className={styles.content}>
              <CheckoutReviewOrderDetailsAddressDisplay address={chosenShippingAddress} isShipping={true} />
            </div>
          ) : (
            <div className={styles.emptyState}>No address provided</div>
          )}
          {!isConfirmationPage && (
            <Link className={styles.editLink} href={`/checkout/shipping`}>
              Edit
            </Link>
          )}
        </div>

        <div className={styles.column}>
          <div className={styles.sectionHeader}>License Information</div>
          {myLicenses.length > 0 ? (
            <div className={styles.content}>
              {!userNameDataLoading && nameData && (
                <div className={styles.name}>
                  {nameData.prefix} {nameData.firstName} {nameData.lastName}
                </div>
              )}
              {myLicenses.map((license) => (
                <div key={license.Number} className={styles.line}>
                  <span>
                    {license.LicenseType.Name.includes("State")
                      ? "State License #"
                      : license.LicenseType.Name.includes("DEA")
                        ? "DEA License #"
                        : license.LicenseType.Name}
                  </span>
                  &nbsp;&nbsp;<span>{license.Number}</span>
                  {license.LicenseType.Name.includes("State") && (
                    <>
                      &nbsp;<span>({license.LicenseAgency.RegionCd})</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>No license provided</div>
          )}
          {!isConfirmationPage && (
            <Link className={styles.editLink} href={`/checkout/license`}>
              Edit
            </Link>
          )}
        </div>

        <div className={styles.column}>
          <div className={styles.sectionHeader}>Payment Method</div>
          {chosenCreditCard && chosenCreditCard.BillingAddress && (
            <div className={styles.content}>
              <div className={styles.cardLine}>
                {path && (
                  <span className={styles.cardLogo}>
                    <Image src={path} alt={alt} width={36} height={24} />
                  </span>
                )}
                <span>
                  <span className={styles.cardType}>{chosenCreditCard.Type}</span> ending in{" "}
                  {chosenCreditCard.Last4Digits}
                </span>
              </div>
              <CheckoutReviewOrderDetailsAddressDisplay address={chosenCreditCard.BillingAddress} isShipping={false} />
            </div>
          )}
          {!isConfirmationPage && (
            <Link className={styles.editLink} href={`/checkout/billing`}>
              Edit
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
